import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Mail, ArrowUpRight, ChevronDown } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";
import { GoldButton } from "./Buttons";
import { EASE } from "./Reveal";
import { NAV_LINKS, RESOURCES_NAV, CONTACT } from "../data/content";
import { useAuth } from "../context/AuthContext";

function ResourcesNav() {
    const { pathname } = useLocation();
    const active = pathname.startsWith("/irs-news") || pathname.startsWith("/resources");
    return (
        <div className="relative group" data-testid="nav-resources">
            <button
                type="button"
                data-testid="nav-resources-trigger"
                aria-haspopup="true"
                className={`flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 ${
                    active ? "text-[#C8A45D]" : "text-white/70 group-hover:text-white"
                }`}
            >
                Resources
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                <div className="bg-[#1C2B45] border border-[#C8A45D]/40 rounded-xl shadow-2xl py-2 min-w-[220px] overflow-hidden">
                    {RESOURCES_NAV.children.map((c) => (
                        <NavLink
                            key={c.path}
                            to={c.path}
                            data-testid={`nav-resources-${c.slug}`}
                            className={({ isActive }) =>
                                `flex items-center justify-between gap-6 px-5 py-3 text-[12px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 ${
                                    isActive ? "text-[#C8A45D] bg-[#C8A45D]/10" : "text-white/75 hover:text-[#C8A45D] hover:bg-white/5"
                                }`
                            }
                        >
                            {c.label}
                            <ArrowUpRight className="h-3.5 w-3.5 text-[#C8A45D]/60" />
                        </NavLink>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Header() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => setOpen(false), [pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                    scrolled ? "bg-[#111318]/90 backdrop-blur-xl border-b border-white/10 shadow-lg" : "bg-gradient-to-b from-[#111318]/95 to-transparent"
                }`}
            >
                <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
                    <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
                        <LogoMark className="h-10 w-10 transition-transform duration-300 group-hover:rotate-6" />
                        <Wordmark />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
                        {NAV_LINKS.filter((l) => l.slug !== "contact").map((l) => (
                            <NavLink
                                key={l.path}
                                to={l.path}
                                end={l.path === "/"}
                                data-testid={`nav-link-${l.slug}`}
                                className={({ isActive }) =>
                                    `text-[12px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 ${
                                        isActive ? "text-[#C8A45D]" : "text-white/70 hover:text-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <ResourcesNav />
                        {NAV_LINKS.filter((l) => l.slug === "contact").map((l) => (
                            <NavLink
                                key={l.path}
                                to={l.path}
                                data-testid={`nav-link-${l.slug}`}
                                className={({ isActive }) =>
                                    `text-[12px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 ${
                                        isActive ? "text-[#C8A45D]" : "text-white/70 hover:text-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to={user ? "/dashboard" : "/login"}
                            data-testid={user ? "nav-link-dashboard" : "nav-link-signin"}
                            className="text-[12px] uppercase tracking-[0.18em] font-medium text-white/70 hover:text-white transition-colors duration-200"
                        >
                            {user ? "Dashboard" : "Sign In"}
                        </Link>
                    </nav>

                    <div className="hidden lg:block">
                        <GoldButton asChild className="h-11 px-6" data-testid="header-get-started">
                            <Link to="/contact">Get Started</Link>
                        </GoldButton>
                    </div>

                    <button
                        className="lg:hidden text-white p-2 -mr-2"
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[60] bg-[#111318]/98 backdrop-blur-xl lg:hidden flex flex-col"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        data-testid="mobile-menu"
                    >
                        <div className="h-20 px-5 flex items-center justify-between border-b border-white/10">
                            <Link to="/" className="flex items-center gap-3" data-testid="mobile-menu-logo">
                                <LogoMark className="h-10 w-10" />
                                <Wordmark />
                            </Link>
                            <button className="text-white p-2 -mr-2" data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                                <X className="h-7 w-7" />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto px-8 py-8 flex flex-col" data-testid="mobile-nav-list">
                            <div className="my-auto flex flex-col gap-1">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.path}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                                >
                                    <NavLink
                                        to={l.path}
                                        end={l.path === "/"}
                                        data-testid={`mobile-nav-link-${l.slug}`}
                                        className={({ isActive }) =>
                                            `font-display text-4xl py-3 flex items-center justify-between border-b border-white/8 ${
                                                isActive ? "text-[#C8A45D]" : "text-white"
                                            }`
                                        }
                                    >
                                        {l.label}
                                        <ArrowUpRight className="h-6 w-6 text-[#C8A45D]/60" />
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, x: -24 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.08 + NAV_LINKS.length * 0.06, duration: 0.5, ease: EASE }}
                                className="pt-4"
                                data-testid="mobile-resources-group"
                            >
                                <p className="text-[11px] uppercase tracking-[0.24em] text-[#C8A45D]/70 px-1 pb-1">Resources</p>
                                {RESOURCES_NAV.children.map((c) => (
                                    <NavLink
                                        key={c.path}
                                        to={c.path}
                                        onClick={() => setOpen(false)}
                                        data-testid={`mobile-nav-link-${c.slug}`}
                                        className={({ isActive }) =>
                                            `font-display text-3xl py-2.5 pl-4 flex items-center justify-between border-b border-white/8 ${
                                                isActive ? "text-[#C8A45D]" : "text-white/85"
                                            }`
                                        }
                                    >
                                        {c.label}
                                        <ArrowUpRight className="h-5 w-5 text-[#C8A45D]/60" />
                                    </NavLink>
                                ))}
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: -24 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.08 + (NAV_LINKS.length + 1) * 0.06, duration: 0.5, ease: EASE }}
                            >
                                <Link
                                    to={user ? "/dashboard" : "/login"}
                                    data-testid={user ? "mobile-nav-link-dashboard" : "mobile-nav-link-signin"}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-4xl py-3 flex items-center justify-between border-b border-white/8 text-[#C8A45D]"
                                >
                                    {user ? "Dashboard" : "Sign In"}
                                    <ArrowUpRight className="h-6 w-6 text-[#C8A45D]/60" />
                                </Link>
                            </motion.div>
                            </div>
                        </nav>
                        <motion.div
                            className="px-8 pb-10 space-y-3"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
                        >
                            <GoldButton asChild className="w-full" data-testid="mobile-get-started">
                                <Link to="/contact">Get Started</Link>
                            </GoldButton>
                            <div className={`grid ${CONTACT.phoneHref ? "grid-cols-2" : "grid-cols-1"} gap-3 pt-2`}>
                                {CONTACT.phoneHref && (
                                    <a href={CONTACT.phoneHref} data-testid="mobile-phone-link" className="flex items-center justify-center gap-2 border border-white/15 h-11 text-white/85 text-sm hover:border-[#C8A45D] hover:text-[#C8A45D] transition-colors duration-200">
                                        <Phone className="h-4 w-4 text-[#C8A45D]" /> Call
                                    </a>
                                )}
                                <a href={CONTACT.emailHref} data-testid="mobile-email-link" className="flex items-center justify-center gap-2 border border-white/15 h-11 text-white/85 text-sm hover:border-[#C8A45D] hover:text-[#C8A45D] transition-colors duration-200">
                                    <Mail className="h-4 w-4 text-[#C8A45D]" /> Email
                                </a>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
